import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { GuaranteeAdjustment } from '../api/guaranteeAdjustment';

/** The guarantee quote for the party size the guest picked, asked once the size has held for 300ms; null until then, or when it is missing or failed. */
export function usePartySizeQuote(
  scope: readonly (string | number | undefined)[],
  partySize: number,
  enabled: boolean,
  fetchQuote: (partySize: number) => Promise<GuaranteeAdjustment | null>,
) {
  const queryClient = useQueryClient();
  const [settled, setSettled] = useState(partySize);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(partySize), 300);
    return () => clearTimeout(timer);
  }, [partySize]);
  const current = enabled && settled === partySize;
  const queryKey = ['partySizeQuote', ...scope, partySize];
  const query = useQuery({
    queryKey,
    queryFn: () => fetchQuote(partySize),
    enabled: current,
    // Pinned, as useBookingQuote's: a refocus or reconnect must not drop the quote the guest is accepting.
    retry: false,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { silentOn: [400, 404], source: 'party-size-quote' },
  });

  return {
    quote: current && query.isSuccess ? query.data : null,
    /** Render the adjustment a refused change answered with. */
    show: (adjustment: GuaranteeAdjustment) => queryClient.setQueryData(queryKey, adjustment),
    /** Ask again: the booking changed under the guest. */
    requote: () => void query.refetch(),
  };
}
